import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserType } from '../../../types/userType';
import { fetchUserByUsername, fetchPostsByUser } from '../../../api/userServices';
import './ProfileDetails.css';
import BackIcon from '../../../assets/icons/icon_back.svg';
import Post from '../PlaygroundTab/Post/Post';
import PostSkeleton from '../PlaygroundTab/Post/PostSkeleton';
import AvatarSkeleton from '../../common/AvatarSkeleton/AvatarSkeleton';
import BrandBadgeIcon from '../../../assets/icons/icon_brand_badge.png';
import VIImage from '../../common/VIImage/VIImage';

const ProfileDetailsSkeleton: React.FC = () => (
  <div className="profile-details">
    <header className="profile-details-header">
      <button className="back-button">
        <img src={BackIcon} alt="Back" className="back-icon" />
      </button>
    </header>
    
    <div className="profile-details-content">
      <div className="profile-header">
        <div className="profile-info">
          <div className="skeleton-username pulse" />
        </div>
        <AvatarSkeleton size={80} />
      </div>

      <div className="profile-bio">
        <div className="skeleton-bio pulse" />
      </div>

      <div className="separator"></div>

      <div className="user-posts">
        <PostSkeleton />
        <PostSkeleton />
        <PostSkeleton />
      </div>
    </div>
  </div>
);

const ProfileDetails: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [userPosts, setUserPosts] = useState<any[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);

  const handlePostDeleted = (postId: number) => {
    setUserPosts(prevPosts => prevPosts.filter(post => post.id !== postId));
  };

  const handleReplyClick = () => {
    console.log('Reply clicked');
  };

  const handlePostClick = useCallback((postSlug: string) => {
    navigate(`/posts/${postSlug}`);
  }, [navigate]);

  useEffect(() => {
    const loadUserData = async () => {
      if (!username) {
        setError('Username not provided');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        // Fetch user data using the username
        const userData = await fetchUserByUsername(username);
        setUser(userData);
        
        // Fetch user posts
        setIsLoadingPosts(true);
        const postsData = await fetchPostsByUser(username);
        setUserPosts(postsData.results);
      } catch (err) {
        setError('Failed to load user profile');
        console.error('Error loading user data:', err);
      } finally {
        setLoading(false);
        setIsLoadingPosts(false);
      }
    };

    loadUserData();
  }, [username]);

  useEffect(() => {
    setCanGoBack(window.history.length > 1);
  }, []);

  if (loading) return <ProfileDetailsSkeleton />;
  if (error) return <div className="error-message">{error}</div>;
  if (!user) return <div className="error-message">User not found</div>;

  return (
    <div className="profile-details">
      <header className="profile-details-header">
        {canGoBack && (
          <button onClick={() => navigate(-1)} className="back-button">
            <img src={BackIcon} alt="Back" className="back-icon" />
          </button>
        )}
      </header>

      <div className="profile-details-content">
        <div className="profile-header">
          <div className="profile-info">
            <h1 className="username">{user.username}</h1>
          </div>
          <div className="profile-avatar-container">
            <VIImage
              src={user.avatar}
              alt={`${user.username}'s avatar`}
              className="profile-avatar"
            />
            {user.account_type === 'brand' && (
              <img 
                src={BrandBadgeIcon} 
                alt="Brand" 
                className="brand-badge" 
              />
            )}
          </div>
        </div>

        <div className="profile-bio">
          <p>{user.bio || 'No bio available'}</p>
        </div>

        <div className="separator"></div>

        <div className="user-posts">
          {isLoadingPosts ? (
            <>
              <PostSkeleton />
              <PostSkeleton />
              <PostSkeleton />
            </>
          ) : userPosts.length > 0 ? (
            userPosts.map((post) => (
              <Post
                key={post.id}
                {...post}
                onPostDeleted={handlePostDeleted}
                currentUser={user}
                currentUserId={user?.id?.toString() ?? ''}
                isNewPost={false}
                onReplyClick={handleReplyClick}
                onClick={() => handlePostClick(post.slug)}
              />
            ))
          ) : (
            <div className="no-posts-message">
              {user.username} has no posts yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileDetails;
